import type { RouteLocationNormalized } from 'vue-router';

import { useAuthStore } from '@/stores/auth';
import { useViewPreferencesStore } from '@/stores/viewPreferences';
import { CombinedGraphQLErrors } from '@apollo/client';
import { useErrorStore } from '@dott/core';
import { getFirstNestJsGraphQLError } from '@pcg/graphql-kit';

const isPublicPage = (route: RouteLocationNormalized): boolean => Boolean(route.meta.public);

/**
 * Auth middleware for cookie-based authentication.
 * With HTTP-only cookies, we can't check token presence/expiration from JS.
 * Instead, we attempt to fetch user and handle 401 errors via Apollo error link.
 */
export const authMiddleware = async (to: RouteLocationNormalized) => {
  const auth = useAuthStore();

  // === PUBLIC PAGES ===
  if (isPublicPage(to)) {
    console.log(`[middlewares/auth] Public page "${String(to.name)}", skipping auth middleware`);

    if (to.meta.anonymous) {
      return;
    }

    if (!auth.user) {
      try {
        console.log('[middlewares/auth] Fetching user data on public page');
        await auth.fetchMe();
      } catch (e) {
        console.error('[middlewares/auth] Error fetching user on public page:', e);
      }
    }

    // Redirect authenticated users away from the login page
    if (auth.user && to.name === 'Login' && !auth.user.id.includes('guest')) {
      return { name: 'Home' };
    }

    return;
  }

  // === FETCH USER IF NEEDED ===
  if (!auth.user) {
    console.log('[middlewares/auth] Fetching user data');

    try {
      await auth.fetchMe();
    } catch (e) {
      console.error('[middlewares/auth] Error fetching user:', e);

      if (CombinedGraphQLErrors.is(e)) {
        const nestJsError = getFirstNestJsGraphQLError(e);
        const statusCode = nestJsError?.extensions?.statusCode;

        // For 403, 500 or unknown errors — show UErrorView without redirect
        if (statusCode === 403 || statusCode === 500 || !statusCode) {
          const errorStore = useErrorStore();
          errorStore.setError({
            statusCode: statusCode ?? 500,
            title: statusCode === 403 ? 'Forbidden' : 'Error',
            description: nestJsError?.message ?? '',
            critical: true,
          });

          return;
        }
      }

      return {
        name: 'Login',
        query: { redirect_uri: to.fullPath },
      };
    }
  }

  // === CHECK AUTH ===
  if ((!auth.user || auth.user.id.includes('guest')) && to.name !== 'Login') {
    console.log('[middlewares/auth] No user or guest, redirecting to login');
    return {
      name: 'Login',
      query: { redirect_uri: to.fullPath },
    };
  }

  // === WARM VIEW PREFERENCES ===
  // Fire-and-forget, in parallel with the navigation — list pages await the
  // store's whenReady() themselves. The store guards against re-fetching, so
  // repeated navigations are free; guests never load.
  if (auth.user && !auth.user.id.includes('guest')) {
    void useViewPreferencesStore().load(auth.user.id);
  }
};
